import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { AlertCircle } from "lucide-react";
import { usePageHeaderStore } from "src/store/pageHeaderStore";
import {
  EnrollmentCurriculumScope,
  EnrollmentCurriculumTarget,
  EnrollmentExamBlueprint,
  EnrollmentProduct,
  EnrollmentStudent,
  manualEnrollmentService,
} from "src/services/manualEnrollmentService";
import { useToast } from "src/hooks/use-toast";
import { ROUTES } from "src/routes/RouteConfig";

// Modular Subcomponents
import { EnrollmentScopeSection } from "src/components/admin/enrollments/EnrollmentScopeSection";
import { EnrollmentCurriculumTree } from "src/components/admin/enrollments/EnrollmentCurriculumTree";
import { EnrollmentPaymentSection } from "src/components/admin/enrollments/EnrollmentPaymentSection";
import { EnrollmentLedgerSidebar } from "src/components/admin/enrollments/EnrollmentLedgerSidebar";

const ManualEnrollment: React.FC = () => {
  const navigate = useNavigate();
  const { toast } = useToast();
  const { setTitle, setDescription, reset } = usePageHeaderStore();

  // Data Sources
  const [students, setStudents] = useState<EnrollmentStudent[]>([]);
  const [products, setProducts] = useState<EnrollmentProduct[]>([]);
  const [scopes, setScopes] = useState<EnrollmentCurriculumScope[]>([]);
  const [targets, setTargets] = useState<EnrollmentCurriculumTarget[]>([]);
  const [blueprints, setBlueprints] = useState<EnrollmentExamBlueprint[]>([]);

  // Form State
  const [studentId, setStudentId] = useState("");
  const [productId, setProductId] = useState("");
  const [scopeId, setScopeId] = useState("");
  const [targetKeys, setTargetKeys] = useState<string[]>([]);
  const [blueprintId, setBlueprintId] = useState("");

  // Commercials & Settlement State
  const [totalSessions, setTotalSessions] = useState("");
  const [actualPricePerSession, setActualPricePerSession] = useState("");
  const [overrideReason, setOverrideReason] = useState("");
  const [paymentKind, setPaymentKind] = useState<"full" | "installment">("full");
  const [installmentAmount, setInstallmentAmount] = useState("");
  const [currency, setCurrency] = useState("USD");
  const [paymentMethod, setPaymentMethod] = useState("");
  const [paymentReference, setPaymentReference] = useState("");
  const [paidAt, setPaidAt] = useState("");
  const [notes, setNotes] = useState("");
  const [invoiceFile, setInvoiceFile] = useState<File | null>(null);

  // Status State
  const [submitting, setSubmitting] = useState(false);
  const [loadingTargets, setLoadingTargets] = useState(false);
  const [submissionError, setSubmissionError] = useState<string | null>(null);

  const product = useMemo(() => products.find((item) => item.id === productId), [productId, products]);

  const boosterObjectiveCount = useMemo(
    () => targets.filter((t) => t.type === "objective").length,
    [targets]
  );

  const boosterDomainCount = useMemo(
    () => targets.filter((t) => t.type === "domain").length,
    [targets]
  );

  const selectedTargets = useMemo(
    () =>
      targets
        .filter((target) => targetKeys.includes(`${target.type}:${target.id}`))
        .map(({ type, id }) => ({ type, id })),
    [targetKeys, targets]
  );

  const selectedScope = useMemo(() => scopes.find((s) => s.id === scopeId), [scopes, scopeId]);

  // Initial Load
  useEffect(() => {
    setTitle("Manual Enrollment & Plan Freeze");
    setDescription(
      "Verify offline payment settlement, resolve canonical curriculum objectives, and activate access in one atomic commit."
    );

    void Promise.all([manualEnrollmentService.fetchStudents(), manualEnrollmentService.fetchProducts()])
      .then(([studentData, productData]) => {
        setStudents(studentData);
        setProducts(productData);
      })
      .catch((error) =>
        toast({
          title: "Unable to load enrollment form",
          description: error.message,
          variant: "destructive",
        })
      );

    return () => reset();
  }, [reset, setDescription, setTitle, toast]);

  // Product Selection Change
  useEffect(() => {
    setScopeId("");
    setTargets([]);
    setTargetKeys([]);
    setBlueprintId("");
    setScopes([]);
    setBlueprints([]);
    setActualPricePerSession(product ? String(product.price) : "");
    setTotalSessions("");
    setInstallmentAmount("");
    setPaymentKind("full");

    if (!product?.subject_id) return;

    void manualEnrollmentService
      .fetchPublishedScopes(product.subject_id)
      .then(setScopes)
      .catch((error) =>
        toast({
          title: "Unable to load curriculum scopes",
          description: error.message,
          variant: "destructive",
        })
      );

    if (product.type === "preparation") {
      void manualEnrollmentService
        .fetchProductExamBlueprints(product.id)
        .then(setBlueprints)
        .catch((error) =>
          toast({
            title: "Unable to load exam blueprints",
            description: error.message,
            variant: "destructive",
          })
        );
    }
  }, [product, toast]);

  // Scope Selection Change (fetch targets for custom plan and booster mode)
  useEffect(() => {
    setTargets([]);
    setTargetKeys([]);

    if (!scopeId || (product?.type !== "custom" && product?.type !== "booster")) return;

    setLoadingTargets(true);
    void manualEnrollmentService
      .fetchScopeTargets(scopeId)
      .then(setTargets)
      .catch((error) =>
        toast({
          title: "Unable to load curriculum",
          description: error.message,
          variant: "destructive",
        })
      )
      .finally(() => setLoadingTargets(false));
  }, [product?.type, scopeId, toast]);

  // Calculations
  const sessions = Number(totalSessions);
  const actualRate = Number(actualPricePerSession);
  const defaultRate = product ? product.price : 0;
  const totalPurchasePrice = defaultRate * sessions;
  const actualPurchasePrice = actualRate * sessions;
  const amountReceived = paymentKind === "full" ? actualPurchasePrice : Number(installmentAmount) || 0;
  const amountDue = Math.max(0, actualPurchasePrice - amountReceived);

  // Target key toggles
  const toggleTarget = (key: string) => {
    setTargetKeys((current) =>
      current.includes(key) ? current.filter((selected) => selected !== key) : [...current, key]
    );
  };

  const selectAllTargets = () => {
    setTargetKeys(targets.map((t) => `${t.type}:${t.id}`));
  };

  const deselectAllTargets = () => {
    setTargetKeys([]);
  };

  const handleInvoiceSelect = (file: File) => {
    const invalid = (file as File & { error?: boolean; errorMessage?: string }).error;
    if (invalid) {
      toast({
        title: "Invalid invoice file",
        description: (file as File & { errorMessage?: string }).errorMessage || "Upload a PDF smaller than 10 MB.",
        variant: "destructive",
      });
      return;
    }
    setInvoiceFile(file);
  };

  // Submission validation
  const canSubmit = Boolean(
    studentId &&
      productId &&
      scopeId &&
      invoiceFile &&
      paymentMethod.trim() &&
      paymentReference.trim() &&
      paidAt &&
      notes.trim() &&
      Number.isInteger(sessions) &&
      sessions >= 1 &&
      Number.isFinite(actualRate) &&
      actualRate > 0 &&
      (product?.type !== "custom" || selectedTargets.length > 0) &&
      (product?.type !== "preparation" || blueprintId) &&
      (paymentKind === "full" ||
        (Number.isFinite(amountReceived) && amountReceived > 0 && amountReceived < actualPurchasePrice))
  );

  const handleSubmit = async (event?: React.FormEvent) => {
    if (event) event.preventDefault();
    setSubmissionError(null);

    const requiredMessage = "Student, product, published scope, payment verification, and invoice PDF are required.";
    if (!product || !invoiceFile || !studentId || !scopeId || !paymentMethod || !paymentReference || !paidAt || !notes) {
      setSubmissionError(requiredMessage);
      toast({ title: "Incomplete fields", description: requiredMessage, variant: "destructive" });
      return;
    }

    if (product.type === "custom" && selectedTargets.length === 0) {
      const message = "Select at least one domain, unit, lesson, or objective for Personalized Plan.";
      setSubmissionError(message);
      toast({ title: "Curriculum target required", description: message, variant: "destructive" });
      return;
    }

    if (product.type === "preparation" && !blueprintId) {
      const message = "Select an approved exam blueprint for Test Preparation.";
      setSubmissionError(message);
      toast({ title: "Exam blueprint required", description: message, variant: "destructive" });
      return;
    }

    if (!Number.isInteger(sessions) || sessions < 1 || !Number.isFinite(actualRate) || actualRate <= 0) {
      const message = "Enter a positive price per session and a whole number of sessions.";
      setSubmissionError(message);
      toast({ title: "Check commercial terms", description: message, variant: "destructive" });
      return;
    }

    const received = paymentKind === "full" ? actualPurchasePrice : Number(installmentAmount);
    if (!Number.isFinite(received) || received <= 0 || (paymentKind === "installment" && received >= actualPurchasePrice)) {
      const message = "The installment amount must be greater than zero and less than the total purchase price.";
      setSubmissionError(message);
      toast({ title: "Check installment payment", description: message, variant: "destructive" });
      return;
    }

    setSubmitting(true);
    try {
      const draft = await manualEnrollmentService.createDraft({
        studentId,
        productId,
        curriculumScopeId: scopeId,
        selectionTargets: selectedTargets,
        examBlueprintId: blueprintId || undefined,
        totalSessions: sessions,
        actualPricePerSession: actualRate,
        overrideReason: overrideReason.trim() || undefined,
        paymentKind,
        installmentAmount: paymentKind === "installment" ? received : undefined,
        currency,
        paymentMethod: paymentMethod.trim(),
        paymentReference: paymentReference.trim(),
        paidAt: new Date(paidAt).toISOString(),
        verificationNotes: notes.trim(),
      });

      const path = await manualEnrollmentService.uploadInvoicePdf(studentId, draft.invoice_id, invoiceFile);
      await manualEnrollmentService.finalize(draft.workflow_id, draft.invoice_id, path);

      toast({
        title: "Student successfully enrolled",
        description: "The curriculum plan is frozen to immutable objectives and the subscription is active.",
      });

      navigate(`${ROUTES.ADMIN_ENROLLMENTS.path}/${draft.workflow_id}`);
    } catch (error) {
      const message = error instanceof Error ? error.message : "The draft was preserved for recovery.";
      setSubmissionError(message);
      toast({
        title: "Enrollment not finalized",
        description: message,
        variant: "destructive",
      });
    } finally {
      setSubmitting(false);
    }
  };

  const scopeDisplayLabel = selectedScope
    ? `${selectedScope.boards?.board_name || selectedScope.board_id || ""} ${
        selectedScope.grades?.grade_label || selectedScope.grade_id || ""
      } (v${selectedScope.version_label})`.trim()
    : undefined;

  return (
    <form onSubmit={handleSubmit} className="space-y-6 pb-12">
      {/* Page Breadcrumb & Status Bar */}
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
        <div className="flex items-center gap-1.5 text-xs text-slate-500">
          <button
            type="button"
            onClick={() => navigate("/admin/dashboard")}
            className="hover:text-purple-700 transition"
          >
            Admin
          </button>
          <span>/</span>
          <button
            type="button"
            onClick={() => navigate(ROUTES.ADMIN_ENROLLMENTS.path)}
            className="hover:text-purple-700 transition font-medium"
          >
            Enrollments
          </button>
          <span>/</span>
          <span className="font-semibold text-slate-800">New Manual Enrollment</span>
        </div>

        <div className="flex items-center gap-2">
          <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium bg-amber-50 text-amber-800 border border-amber-200">
            <span className="w-1.5 h-1.5 rounded-full bg-amber-500 animate-pulse" />
            Draft In-Progress
          </span>
        </div>
      </div>

      {/* Submission Error Banner */}
      {submissionError && (
        <div role="alert" className="rounded-xl border border-red-200 bg-red-50 p-4 text-xs text-red-900 flex items-start gap-3">
          <AlertCircle className="w-5 h-5 text-red-600 shrink-0 mt-0.5" />
          <div className="space-y-1">
            <p className="font-bold">Manual enrollment cannot be finalized</p>
            <p className="break-words leading-relaxed text-red-800">{submissionError}</p>
          </div>
        </div>
      )}

      {/* 2-Column Split Executive Ledger Layout */}
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-6 items-start">
        {/* Left Column: Operational Steps (7 of 12 cols = ~58%) */}
        <div className="lg:col-span-7 space-y-6">
          {/* Step 1: Student & Scope Section */}
          <EnrollmentScopeSection
            students={students}
            studentId={studentId}
            onStudentChange={setStudentId}
            products={products}
            productId={productId}
            onProductChange={setProductId}
            scopes={scopes}
            scopeId={scopeId}
            onScopeChange={setScopeId}
            selectedProduct={product}
            objectiveCount={product?.type === "booster" ? boosterObjectiveCount : undefined}
            domainCount={product?.type === "booster" ? boosterDomainCount : undefined}
          />

          {/* Step 2: Canonical Curriculum Specification */}
          <EnrollmentCurriculumTree
            productType={product?.type}
            scopeId={scopeId}
            targets={targets}
            targetKeys={targetKeys}
            onToggleTarget={toggleTarget}
            onSelectAll={selectAllTargets}
            onDeselectAll={deselectAllTargets}
            blueprints={blueprints}
            blueprintId={blueprintId}
            onBlueprintChange={setBlueprintId}
            isLoadingTargets={loadingTargets}
          />

          {/* Step 3: Commercials & Payment Verification */}
          <EnrollmentPaymentSection
            defaultPrice={defaultRate}
            totalSessions={totalSessions}
            onTotalSessionsChange={setTotalSessions}
            actualPricePerSession={actualPricePerSession}
            onActualPriceChange={setActualPricePerSession}
            overrideReason={overrideReason}
            onOverrideReasonChange={setOverrideReason}
            paymentKind={paymentKind}
            onPaymentKindChange={setPaymentKind}
            installmentAmount={installmentAmount}
            onInstallmentAmountChange={setInstallmentAmount}
            currency={currency}
            onCurrencyChange={setCurrency}
            paymentMethod={paymentMethod}
            onPaymentMethodChange={setPaymentMethod}
            paymentReference={paymentReference}
            onPaymentReferenceChange={setPaymentReference}
            paidAt={paidAt}
            onPaidAtChange={setPaidAt}
            notes={notes}
            onNotesChange={setNotes}
            invoiceFile={invoiceFile}
            onInvoiceSelect={handleInvoiceSelect}
            onInvoiceRemove={() => setInvoiceFile(null)}
          />
        </div>

        {/* Right Column: Sticky Executive Ledger Sidebar (5 of 12 cols = ~42%) */}
        <div className="lg:col-span-5">
          <EnrollmentLedgerSidebar
            totalPurchasePrice={totalPurchasePrice}
            actualPurchasePrice={actualPurchasePrice}
            amountReceived={amountReceived}
            amountDue={amountDue}
            currency={currency}
            totalSessions={sessions}
            actualRate={actualRate}
            catalogRate={defaultRate}
            productType={product?.type}
            targetCount={
              product?.type === "booster" ? boosterObjectiveCount : selectedTargets.length
            }
            scopeLabel={scopeDisplayLabel}
            isSubmitting={submitting}
            canSubmit={canSubmit}
            onSubmit={handleSubmit}
          />
        </div>
      </div>
    </form>
  );
};

export default ManualEnrollment;
